import { Link, NavLink, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_LEVEL } from '../../utils/constants';
import { Building2, Users, Palette, HardDrive, ScrollText, Shield, ShieldCheck, Settings as SettingsIcon, CalendarClock, HeartPulse } from 'lucide-react';

const TABS = [
  { to: 'organizations',   label: 'Organizations', icon: Building2, minRole: 'admin' },
  { to: 'users',           label: 'Users & Access', icon: Users,    perm: 'settings.users' },
  { to: 'roles',           label: 'Roles & Permissions', icon: ShieldCheck, minRole: 'org_admin' },
  { to: 'branding',        label: 'Branding & Preferences', icon: Palette, perm: 'settings.branding' },
  { to: 'security',        label: 'Security', icon: Shield, perm: 'settings.org' },
  { to: 'storage',         label: 'Storage & Files', icon: HardDrive, minRole: 'admin' },
  { to: 'logs',            label: 'Activity Logs', icon: ScrollText, perm: 'settings.logs' },
  { to: 'reports',         label: 'Scheduled Reports', icon: CalendarClock, perm: 'settings.notifications' },
  { to: 'data-health',     label: 'Data Health', icon: HeartPulse, perm: 'settings.org' },
];

const canSee = (t, userLevel, hasPermission) =>
  t.perm ? hasPermission(t.perm) : userLevel >= (ROLE_LEVEL[t.minRole] || 99);

/** Redirects /settings → first tab the current user can actually access. */
export function SettingsIndexRedirect() {
  const { user, impersonatingRole, hasPermission } = useAuth();
  const effectiveRole = impersonatingRole || user?.role || 'trainer';
  const userLevel = ROLE_LEVEL[effectiveRole] || 0;
  const firstVisible = TABS.find(t => canSee(t, userLevel, hasPermission));
  return <Navigate to={firstVisible ? firstVisible.to : 'users'} replace />;
}

export default function SettingsLayout() {
  const { user, impersonatingRole, hasPermission } = useAuth();
  const effectiveRole = impersonatingRole || user?.role || 'trainer';
  const userLevel = ROLE_LEVEL[effectiveRole] || 0;

  const visible = TABS.filter(t => canSee(t, userLevel, hasPermission));

  return (
    <div className="space-y-5 relative" data-testid="settings-page">
      <div aria-hidden className="page-blob bg-[var(--lumyn-settings)]" />
      <div className="relative z-10 space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-settings)]/10 text-[var(--lumyn-settings)] text-[11px] font-semibold uppercase tracking-[0.14em]">
              <SettingsIcon className="w-3.5 h-3.5" />Engine room
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-sm text-muted-foreground">Org, users, branding, storage, logs, scheduled reports.</p>
          </div>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to dashboard</Link>
        </div>

        <div className="flex gap-1 border-b overflow-x-auto">
          {visible.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                `relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`
              }
              data-testid={`settings-tab-${t.to}`}
            >
              {({ isActive }) => (
                <>
                  <t.icon className={`w-4 h-4 ${isActive ? 'text-[var(--lumyn-settings)]' : ''}`} />
                  {t.label}
                  {isActive && (
                    <span aria-hidden className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--lumyn-settings)] rounded-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <Outlet />
      </div>
    </div>
  );
}
